import { useState, useEffect, useCallback, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AuthContext } from '@/lib/auth/models/auth.model';

export interface OrphanItinerary {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  budget: number;
  description: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  image: string | null;
  currency: string | null;
  total: number | null;
}

export interface CreateOrphanItineraryPayload {
  title: string;
  start_date: string;
  end_date: string;
  budget: number;
  description?: string;
  image?: string;
}

export const useOrphanItineraries = () => {
  const [orphanItineraries, setOrphanItineraries] = useState<OrphanItinerary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useOrphanItineraries must be used within an AuthProvider');
  }

  const { user } = authContext;

  // Fetch orphan itineraries accessible to the current user (RLS policies handle filtering)
  const fetchOrphanItineraries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        setOrphanItineraries([]);
        setLoading(false);
        return;
      }

      // Query orphan itineraries - RLS policies will filter based on user role:
      // - Admins: see all
      // - Creators: see their own orphan itineraries
      // - Agency users: see orphans created by themselves and their members (via RLS)
      const { data, error: fetchError } = await supabase
        .from('itineraries')
        .select('*')
        .is('travel_request_id', null)
        .order('created_at', { ascending: false });

      if (fetchError) {
        const errorMsg = t('playground.loadError');
        setError(errorMsg);
        toast({
          title: t('common.error'),
          description: errorMsg,
          variant: 'destructive',
        });
        return;
      }

      setOrphanItineraries(data || []);
    } catch (err) {
      const errorMsg = t('errors.generic');
      setError(errorMsg);
      toast({
        title: t('common.error'),
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Create a new orphan itinerary
  const createOrphanItinerary = useCallback(async (payload: CreateOrphanItineraryPayload) => {
    try {
      if (!user?.id) {
        toast({
          title: t('common.error'),
          description: t('auth.loginRequired'),
          variant: 'destructive',
        });
        return null;
      }

      const itineraryData = {
        title: payload.title,
        start_date: payload.start_date,
        end_date: payload.end_date,
        budget: payload.budget,
        description: payload.description || '',
        travel_request_id: null,
        status: 'draft',
        image: payload.image || '',
        guests_data: [],
        total: payload.budget,
      };

      // Use save_itinerary RPC (SECURITY DEFINER) to bypass RLS on direct INSERT,
      // which can fail for travel_advisor_member due to auth.uid() resolution issues.
      const { data, error: insertError } = await supabase
        .rpc('save_itinerary', { p_itinerary: itineraryData });

      if (insertError) {
        toast({
          title: t('common.error'),
          description: t('playground.createError'),
          variant: 'destructive',
        });
        return null;
      }

      toast({
        variant: "success",
        title: t("common.success"),
        description: t("playground.createSuccess"),
      });

      // Refresh the list
      await fetchOrphanItineraries();

      return data;
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: 'destructive',
      });
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Link an orphan itinerary to a travel request (duplicate and link)
  const linkItineraryToTravelRequest = useCallback(async (
    itineraryId: string,
    travelRequestId: string
  ) => {
    try {
      const { data, error: rpcError } = await supabase.rpc('duplicate_itinerary_for_travel_request', {
        source_itinerary_id: itineraryId,
        target_travel_request_id: travelRequestId,
      });

      if (rpcError) {
        toast({
          title: t('common.error'),
          description: t('playground.linkError'),
          variant: 'destructive',
        });
        return null;
      }

      toast({ variant: 'success',
        title: t('common.success'),
        description: t('playground.linkSuccess'),
      });

      return data;
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: 'destructive',
      });
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Delete an orphan itinerary
  const deleteOrphanItinerary = useCallback(async (itineraryId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('itineraries')
        .delete()
        .eq('id', itineraryId);

      if (deleteError) {
        toast({
          title: t('common.error'),
          description: t('playground.deleteError'),
          variant: 'destructive',
        });
        return false;
      }

      toast({ variant: 'success',
        title: t('common.success'),
        description: t('playground.deleteSuccess'),
      });

      // Refresh the list
      await fetchOrphanItineraries();

      return true;
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: 'destructive',
      });
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fetch on mount and when user changes
  // Auto-fetch on mount
  useEffect(() => {
    fetchOrphanItineraries();
  }, [fetchOrphanItineraries]);

  return {
    orphanItineraries,
    loading,
    error,
    refreshData: fetchOrphanItineraries,
    createOrphanItinerary,
    linkItineraryToTravelRequest,
    deleteOrphanItinerary,
  };
};
